// ** react imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// ** router imports
import { useParams } from 'react-router-dom';

// ** custom hook imports
import { useGetCustomerDetailsById } from '../../../../hooks/apis/useUsers';

// ** utils imports
import { formatPhoneNumber, formatUtcToLocal } from '../../../../utils/Utils';

// ** component imports
import Loading from '../../../../components/common/Loading';

// ** utility function to render values
const renderValue = value => {
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    value === 'undefined undefined' // only for browser
  ) {
    return '-';
  }
  return value;
};

const CustomerBasicInfo = props => {
  // hooks
  const { id } = useParams();
  const [copiedField, setCopiedField] = useState(null);

  // custom hooks
  const { data, isGetCustomerDetailsByIdLoading, getCustomerDetailsById } =
    useGetCustomerDetailsById();

  // hooks
  useEffect(() => {
    if (id) {
      getCustomerDetailsById(id);
    }
  }, [id]);

  useEffect(() => {
    if (data !== undefined) {
      console.log('customer details by id data', data);
    }
  }, [data]);

  // Copy to clipboard function
  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // render
  return (
    <div className="w-full h-full">
      <div className="bg-white h-[calc(75vh)] dark:bg-gray-800 shadow-xs rounded-xl relative overflow-y-auto">
        {isGetCustomerDetailsByIdLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
              <Loading />
            </div>
          </div>
        )}

        {data && (
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* User Information Card */}
              <div className="bg-white dark:bg-gray-700/30 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  User Information
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User ID
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 font-mono mt-1">
                        {data.userId}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(data.userId, 'userId')}
                      className="text-gray-40 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedField === 'userId' ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-sm">📋</span>
                      )}
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Full Name
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                      {renderValue(data.fullName)}
                    </p>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                        {renderValue(data.email)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {data.email && (
                        <>
                          <a
                            href={`mailto:${data.email}`}
                            className="text-gray-400 hover:text-blue-500 transition-colors"
                            title="Send email"
                          >
                            <span className="text-sm">✉️</span>
                          </a>
                          <button
                            onClick={() => copyToClipboard(data.email, 'email')}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Copy to clipboard"
                          >
                            {copiedField === 'email' ? (
                              <span className="text-green-500">✓</span>
                            ) : (
                              <span className="text-sm">📋</span>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Phone
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                        {data.phone
                          ? formatPhoneNumber(data.phone)
                          : renderValue(data.phone)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {data.phone && (
                        <>
                          <a
                            href={`tel:${data.phone}`}
                            className="text-gray-400 hover:text-green-500 transition-colors"
                            title="Call"
                          >
                            <span className="text-sm">📞</span>
                          </a>
                          <button
                            onClick={() =>
                              copyToClipboard(`${data.phone}`, 'phone')
                            }
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Copy to clipboard"
                          >
                            {copiedField === 'phone' ? (
                              <span className="text-green-500">✓</span>
                            ) : (
                              <span className="text-sm">📋</span>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Role
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 capitalize">
                        {renderValue(data.role)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Account Type
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 capitalize">
                        {renderValue(data.accountType)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Device Information Card */}
              <div className="bg-white dark:bg-gray-700/30 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  Device Information
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Device Type
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                        {renderValue(data.deviceType)}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        copyToClipboard(data.deviceType, 'deviceType')
                      }
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedField === 'deviceType' ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-sm">📋</span>
                      )}
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Operating System
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                      {data.os && data.osVersion
                        ? `${data.os} ${data.osVersion}`
                        : renderValue(data.os)}
                    </p>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Browser
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                        {renderValue(data.browserInfo)}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        copyToClipboard(data.browserInfo, 'browserInfo')
                      }
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedField === 'browserInfo' ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-sm">📋</span>
                      )}
                    </button>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        IP Address
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 font-mono">
                        {renderValue(data.ipAddress)}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        copyToClipboard(data.ipAddress, 'ipAddress')
                      }
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedField === 'ipAddress' ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-sm">📋</span>
                      )}
                    </button>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Device ID
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 font-mono">
                        {renderValue(data.deviceId)}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(data.deviceId, 'deviceId')}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedField === 'deviceId' ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-sm">📋</span>
                      )}
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      App Version
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                      {renderValue(data.appVersion)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status & Timestamps Card */}
              <div className="bg-white dark:bg-gray-700/30 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 lg:col-span-2 xl:col-span-1">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  Status & Timestamps
                </h3>

                {/* Status Badges */}
                <div className="mb-6">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Account Status
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        data.isEmailVerified
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {data.isEmailVerified ? '✓' : '✗'} Email{' '}
                      {data.isEmailVerified ? 'Verified' : 'Not Verified'}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        data.isPasswordSet
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {data.isPasswordSet ? '✓' : '✗'} Password{' '}
                      {data.isPasswordSet ? 'Set' : 'Not Set'}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        data.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {data.isActive ? '✓' : '✗'}{' '}
                      {data.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created At
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                      {data.createdAt
                        ? formatUtcToLocal(data.createdAt)
                        : renderValue(data.createdAt)}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Updated At
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                      {data.updatedAt
                        ? formatUtcToLocal(data.updatedAt)
                        : renderValue(data.updatedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CustomerBasicInfo.propTypes = {};

export default CustomerBasicInfo;
